import React, { useState, useRef, useEffect } from 'react';
import { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat } from '@zxing/library';
import { parseAAMVA } from '../../utils/aamvaParser';
import Modal from '../Modal';
import Button from '../Button';
import './DriverLicenseScanner.css';

const DriverLicenseScanner = ({ onScanComplete, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [scanMode, setScanMode] = useState('camera'); // 'camera' or 'upload'
  const [uploadedImage, setUploadedImage] = useState(null);
  const [capturedFrame, setCapturedFrame] = useState(null);
  const [activeLibrary, setActiveLibrary] = useState('zxing'); // 'zxing' or 'quagga'
  const [useNativeDetector, setUseNativeDetector] = useState(false);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const quaggaInitialized = useRef(false);
  const nativeDetectorRef = useRef(null);
  
  // Function to play beep sound when barcode is detected
  const playBeepSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Beep frequency (Hz)
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (e) {
      console.warn('Could not play beep sound:', e);
    }
  };

  useEffect(() => {
    console.log('=== INITIALIZING SCANNER ===');
    
    // Try native BarcodeDetector API first (better PDF417 support)
    // eslint-disable-next-line no-undef
    if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
      try {
        // eslint-disable-next-line no-undef
        nativeDetectorRef.current = new window.BarcodeDetector({
          formats: ['pdf417', 'qr_code', 'code_128', 'code_39']
        });
        setUseNativeDetector(true);
        console.log('✅ Using native BarcodeDetector API (better PDF417 support)');
      } catch (e) {
        console.warn('Native BarcodeDetector not supported:', e);
        setUseNativeDetector(false);
      }
    } else {
      setUseNativeDetector(false);
    }
    
    // Always initialize ZXing as fallback (even if native is available)
    try {
      const hints = new Map();
      // Enable PDF417_TRY_HARDER for better PDF417 detection
      hints.set(DecodeHintType.TRY_HARDER, true);
      // Also try all formats
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417, BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX]);
      
      codeReaderRef.current = new BrowserMultiFormatReader(hints);
      // Slower scanning for better accuracy
      codeReaderRef.current.timeBetweenScansMillis = 200;
      console.log('✅ ZXing scanner initialized (as fallback)');
    } catch (e) {
      console.error('Failed to initialize ZXing scanner with hints:', e);
      // Fallback without hints
      try {
        codeReaderRef.current = new BrowserMultiFormatReader();
        codeReaderRef.current.timeBetweenScansMillis = 200;
        console.log('✅ ZXing scanner initialized with fallback settings');
      } catch (e2) {
        console.error('Failed to initialize ZXing scanner:', e2);
      }
    }

    // Don't try to list devices on mount - we'll do it after permission is granted
    // Some browsers don't support enumerating devices without permission first
    console.log('Scanner initialized. Devices will be listed after camera permission is granted.');

    return () => {
      // Cleanup on unmount
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }
    };
  }, []);

  const startScanning = async () => {
    console.log('=== START SCANNING CALLED ===');
    console.log('Selected device:', selectedDeviceId);
    console.log('Video ref:', videoRef.current);
    console.log('Code reader:', codeReaderRef.current);
    console.log('Native detector:', nativeDetectorRef.current);
    console.log('Use native detector:', useNativeDetector);

    // Check if we have either native detector or ZXing initialized
    if (!useNativeDetector && !codeReaderRef.current) {
      setError('Scanner not initialized. Please refresh the page.');
      console.error('Code reader not initialized');
      return;
    }
    
    if (useNativeDetector && !nativeDetectorRef.current && !codeReaderRef.current) {
      setError('Scanner not initialized. Please refresh the page.');
      console.error('Neither native detector nor ZXing initialized');
      return;
    }

    if (!videoRef.current) {
      setError('Video element not ready. Please try again.');
      console.error('Video ref not available');
      return;
    }

    try {
      setError(null);
      
      // Check if we're on HTTPS or localhost (required for camera access)
      const isSecureContext = window.isSecureContext || 
                              window.location.protocol === 'https:' || 
                              window.location.hostname === 'localhost' || 
                              window.location.hostname === '127.0.0.1';
      
      if (!isSecureContext) {
        setIsScanning(false);
        setError('Camera scanning requires HTTPS (or localhost). Your current connection is HTTP.\n\nPlease use the "Upload Image" option instead, or enable SSL/HTTPS on your server.');
        console.warn('Camera access blocked: Not a secure context (HTTPS required)');
        return;
      }
      
      setIsScanning(true);
      console.log('Starting scan...');
      
      // Request camera with high quality constraints for better barcode detection
      let deviceId = selectedDeviceId;
      let stream = null;
      
      try {
        // First, request camera access with high quality constraints + torch
        const constraints = {
          video: {
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            facingMode: 'environment', // Back camera (required, not just ideal)
            focusMode: 'continuous',
            torch: true // Enable torch/flash for better lighting
          }
        };
        
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log('✅ Camera stream obtained with high quality');
        
        // Set the stream to video element
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          console.log('Video stream started');
        }
        
        // Try to list devices to find back camera
        try {
          const videoInputDevices = await codeReaderRef.current.listVideoInputDevices();
          console.log('Available devices:', videoInputDevices);
          if (videoInputDevices.length > 0) {
            setDevices(videoInputDevices);
            
            // Prefer back camera (environment facing)
            const backCamera = videoInputDevices.find(device => 
              device.label.toLowerCase().includes('back') || 
              device.label.toLowerCase().includes('rear') ||
              device.label.toLowerCase().includes('environment')
            );
            
            if (backCamera) {
              deviceId = backCamera.deviceId;
              setSelectedDeviceId(deviceId);
              console.log('✅ Using back camera:', backCamera.label);
            } else {
              deviceId = selectedDeviceId || videoInputDevices[0].deviceId;
              setSelectedDeviceId(deviceId);
              console.log('Using first available camera:', videoInputDevices[0].label);
            }
          }
        } catch (deviceError) {
          console.log('Could not list devices (will use current stream):', deviceError);
        }
      } catch (mediaError) {
        console.error('Error getting media stream:', mediaError);
        // Fallback: let scanner handle it
        try {
          const videoInputDevices = await codeReaderRef.current.listVideoInputDevices();
          if (videoInputDevices.length > 0) {
            setDevices(videoInputDevices);
            const backCamera = videoInputDevices.find(device => 
              device.label.toLowerCase().includes('back') || 
              device.label.toLowerCase().includes('rear') ||
              device.label.toLowerCase().includes('environment')
            );
            if (backCamera) {
              deviceId = backCamera.deviceId;
              setSelectedDeviceId(deviceId);
            } else {
              deviceId = selectedDeviceId || videoInputDevices[0].deviceId;
              setSelectedDeviceId(deviceId);
            }
          }
        } catch (deviceError) {
          console.log('Could not list devices:', deviceError);
        }
      }

      // Start scanning from video stream
      // Use decodeFromVideoDevice for continuous scanning
      // This will request camera permission if not already granted
      console.log('Starting video device scan with device:', deviceId || 'default');
      console.log('Video element:', videoRef.current);
      console.log('Video dimensions:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight);
      console.log('Scanner settings:', {
        timeBetweenScans: codeReaderRef.current.timeBetweenScansMillis
      });
      
      // Wait for video to be ready with actual dimensions
      await new Promise((resolve) => {
        const checkReady = () => {
          if (videoRef.current && videoRef.current.readyState >= 2 && videoRef.current.videoWidth > 0) {
            console.log('Video ready. Dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            resolve();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
        // Timeout after 3 seconds
        setTimeout(resolve, 3000);
      });
      
      if (!videoRef.current || videoRef.current.videoWidth === 0) {
        setError('Video stream not ready. Please try again.');
        setIsScanning(false);
        return;
      }
      
      console.log('Video ready. Final dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
      
      // Skip QuaggaJS for now (has build issues) - use ZXing directly
      console.log('🔄 Using ZXing for scanning...');
      setActiveLibrary('zxing');
      const quaggaStarted = false;
      
      // If QuaggaJS didn't start, use ZXing as fallback
      if (!quaggaStarted) {
        console.log('🔄 Starting ZXing scanning (fallback)...');
        setActiveLibrary('zxing');
        
        // Try using continuous scanning with canvas-based approach for better detection
        let scanInterval = null;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const scanFrame = async () => {
        if (!videoRef.current || !isScanning) return;
        
        const video = videoRef.current;
        if (video.readyState < 2 || video.videoWidth === 0) return;
        
        try {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          let result = null;
          
          // Try native BarcodeDetector first if available
          if (useNativeDetector && nativeDetectorRef.current) {
            try {
              const barcodes = await nativeDetectorRef.current.detect(canvas);
              if (barcodes && barcodes.length > 0) {
                // Native API returns different format, convert it
                const barcode = barcodes[0];
                result = {
                  getText: () => barcode.rawValue,
                  getBarcodeFormat: () => barcode.format
                };
                console.log('✅ Native BarcodeDetector found barcode:', barcode.format);
              }
            } catch (nativeErr) {
              console.log('Native detector failed, trying ZXing...', nativeErr);
            }
          }
          
          // Fallback to ZXing if native didn't work
          if (!result && codeReaderRef.current) {
            // Apply enhanced contrast canvas for PDF417
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            
            for (let i = 0; i < data.length; i += 4) {
              const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
              const contrast = 2.0;
              const adjusted = ((gray - 128) * contrast) + 128;
              const clamped = Math.max(0, Math.min(255, adjusted));
              data[i] = clamped;
              data[i + 1] = clamped;
              data[i + 2] = clamped;
            }
            ctx.putImageData(imageData, 0, 0);
            
            // Convert canvas to Image element for decoding
            const img = new Image();
            img.src = canvas.toDataURL('image/png', 1.0);
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              setTimeout(() => reject(new Error('Image load timeout')), 2000);
            });
            
            result = await codeReaderRef.current.decodeFromImage(img);
          }
          
          if (result) {
            console.log('=== ✅ BARCODE DETECTED FROM FRAME ===');
            console.log('Result:', result);
            console.log('Format:', result.getBarcodeFormat());
            
            // Stop scanning
            if (scanInterval) {
              clearInterval(scanInterval);
              scanInterval = null;
            }
            stopScanning();
            
            const barcodeText = result.getText();
            console.log('✅ Barcode detected!');
            console.log('Raw text:', barcodeText);
            console.log('Barcode format:', result.getBarcodeFormat());
            console.log('Barcode length:', barcodeText.length);

            // Parse AAMVA data
            const parsedData = parseAAMVA(barcodeText);
            console.log('Parsed data:', parsedData);

            // Check if we got any useful data
            const hasData = parsedData && (
              parsedData.firstName || 
              parsedData.lastName || 
              parsedData.driversLicense || 
              parsedData.address ||
              parsedData.city ||
              parsedData.state ||
              parsedData.zipcode
            );

            if (hasData) {
              console.log('✅ Successfully extracted data, calling onScanComplete');
              // Play beep sound
              playBeepSound();
              // Call callback with parsed data
              if (onScanComplete) {
                onScanComplete(parsedData);
              }
            } else {
              // Show the raw barcode data for debugging
              console.warn('⚠️ Barcode detected but could not parse data');
              console.warn('Raw data (first 500 chars):', barcodeText.substring(0, 500));
              console.warn('Full barcode length:', barcodeText.length);
              console.warn('Parsed data result:', parsedData);
              
              // Try to show a more helpful error
              if (barcodeText && barcodeText.length > 50) {
                setError(`Barcode detected (${barcodeText.length} chars) but could not parse. This may be a non-standard format. Check console for details.`);
              } else {
                setError('Could not parse driver license data. The barcode may be damaged or in an unsupported format. Please try scanning again.');
              }
            }
          }
        } catch (err) {
          // NotFoundException is normal - no barcode in this frame
          if (err.name !== 'NotFoundException') {
            console.error('Frame scan error:', err.name, err.message);
          }
        }
        };
        
        // Start continuous frame scanning
        scanInterval = setInterval(scanFrame, 100); // Scan every 100ms
        
        // Also try the built-in method as fallback
        const scanResult = codeReaderRef.current.decodeFromVideoDevice(
        deviceId || undefined,
        videoRef.current,
        (result, error) => {
          if (result) {
            console.log('=== ✅ BARCODE DETECTED (built-in method) ===');
            console.log('Result:', result);
            console.log('Format:', result.getBarcodeFormat());
            
            // Stop canvas scanning
            if (scanInterval) {
              clearInterval(scanInterval);
              scanInterval = null;
            }
            
            stopScanning();
            
            const barcodeText = result.getText();
            console.log('✅ Barcode detected!');
            console.log('Raw text:', barcodeText);
            console.log('Barcode format:', result.getBarcodeFormat());
            console.log('Barcode length:', barcodeText.length);

            // Parse AAMVA data
            const parsedData = parseAAMVA(barcodeText);
            console.log('Parsed data:', parsedData);

            // Check if we got any useful data
            const hasData = parsedData && (
              parsedData.firstName || 
              parsedData.lastName || 
              parsedData.driversLicense || 
              parsedData.address ||
              parsedData.city ||
              parsedData.state ||
              parsedData.zipcode
            );

            if (hasData) {
              console.log('✅ Successfully extracted data, calling onScanComplete');
              // Play beep sound
              playBeepSound();
              // Call callback with parsed data
              if (onScanComplete) {
                onScanComplete(parsedData);
              }
            } else {
              // Show the raw barcode data for debugging
              console.warn('⚠️ Barcode detected but could not parse data');
              console.warn('Raw data (first 500 chars):', barcodeText.substring(0, 500));
              console.warn('Full barcode length:', barcodeText.length);
              console.warn('Parsed data result:', parsedData);
              
              // Try to show a more helpful error
              if (barcodeText && barcodeText.length > 50) {
                setError(`Barcode detected (${barcodeText.length} chars) but could not parse. This may be a non-standard format. Check console for details.`);
              } else {
                setError('Could not parse driver license data. The barcode may be damaged or in an unsupported format. Please try scanning again.');
              }
            }
          }
          // Don't log NotFoundException - it's normal and happens continuously
          if (error && error.name !== 'NotFoundException') {
            console.log('=== SCAN ERROR ===');
            console.log('Error name:', error.name);
            console.log('Error message:', error.message);
          }

          if (result) {
            // Barcode detected
            const barcodeText = result.getText();
            console.log('✅ Barcode detected!');
            console.log('Raw text:', barcodeText);
            console.log('Barcode format:', result.getBarcodeFormat());
            console.log('Barcode length:', barcodeText.length);

            // Parse AAMVA data
            const parsedData = parseAAMVA(barcodeText);
            console.log('Parsed data:', parsedData);

            // Check if we got any useful data
            const hasData = parsedData && (
              parsedData.firstName || 
              parsedData.lastName || 
              parsedData.driversLicense || 
              parsedData.address ||
              parsedData.city ||
              parsedData.state ||
              parsedData.zipcode
            );

            if (hasData) {
              // Stop scanning
              stopScanning();
              
              console.log('✅ Successfully extracted data, calling onScanComplete');
              // Call callback with parsed data
              if (onScanComplete) {
                onScanComplete(parsedData);
              }
            } else {
              // Show the raw barcode data for debugging
              console.warn('⚠️ Barcode detected but could not parse data');
              console.warn('Raw data (first 500 chars):', barcodeText.substring(0, 500));
              console.warn('Full barcode length:', barcodeText.length);
              console.warn('Parsed data result:', parsedData);
              
              // Try to show a more helpful error
              if (barcodeText && barcodeText.length > 50) {
                setError(`Barcode detected (${barcodeText.length} chars) but could not parse. This may be a non-standard format. Check console for details.`);
              } else {
                setError('Could not parse driver license data. The barcode may be damaged or in an unsupported format. Please try scanning again.');
              }
            }
          }
          if (error) {
            // Log errors less frequently to avoid console spam
            // NotFoundException is normal - it means no barcode found yet (happens continuously)
            if (error.name !== 'NotFoundException') {
              console.error('Scanning error:', error.name, error.message);
              if (error.name === 'FormatException' || error.name === 'ChecksumException') {
                // Don't show error immediately - let user try to position better
                // setError('Could not read barcode. Please ensure the barcode is clearly visible, well-lit, and try again.');
              }
            }
          }
        }
        );
        console.log('Scan started, result:', scanResult);
      
        // Store interval reference for cleanup
        if (scanInterval) {
          window.scanInterval = scanInterval; // For debugging
        }
      } // End of if (!quaggaStarted)
    } catch (err) {
      console.error('❌ Error starting scan:', err);
      console.error('Error stack:', err.stack);
      setIsScanning(false);
      
      // Provide helpful error messages based on error type
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please:\n1. Click the camera/lock icon in your browser\'s address bar\n2. Select "Allow" for camera access\n3. Click "Start Scanning" again');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please connect a camera device and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is being used by another application. Please close other apps using the camera and try again.');
      } else if (err.message && err.message.includes('permission')) {
        setError('Camera permission required. Please allow camera access in your browser settings and try again.');
      } else {
        setError('Failed to start scanning: ' + (err.message || 'Unknown error') + '. Please check camera permissions and try again.');
      }
    }
  };

  // QuaggaJS scanning function (disabled due to build issues)
  const startQuaggaScanning = async (deviceId) => {
    // Skip QuaggaJS for now due to build/constructor errors
    // Will use ZXing instead
    throw new Error('QuaggaJS temporarily disabled due to build issues');
  };

  const stopScanning = () => {
    // Stop ZXing
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (e) {
        console.warn('Error resetting ZXing:', e);
      }
    }
    
    // Stop QuaggaJS (disabled)
    if (quaggaInitialized.current) {
      quaggaInitialized.current = false;
      console.log('QuaggaJS cleanup (disabled)');
    }
    
    // Clear any canvas-based scanning intervals
    if (window.scanInterval) {
      clearInterval(window.scanInterval);
      window.scanInterval = null;
    }
    
    setIsScanning(false);
  };

  const handleClose = () => {
    stopScanning();
    setCapturedFrame(null);
    if (onClose) {
      onClose();
    }
  };

  const captureFrame = async () => {
    if (!videoRef.current || !isScanning) return;
    
    try {
      setError(null);
      const video = videoRef.current;
      
      // Use actual video dimensions for better quality
      const width = video.videoWidth || video.clientWidth;
      const height = video.videoHeight || video.clientHeight;
      
      if (width === 0 || height === 0) {
        setError('Video not ready. Please wait a moment and try again.');
        return;
      }
      
      console.log('Capturing frame:', width, 'x', height);
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, width, height);
      
      // Apply image enhancement (same as upload image)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Aggressive preprocessing for PDF417 (based on GitHub issue #289 workarounds)
      // PDF417 needs high contrast and sharp edges
      // Enhanced contrast canvas for better PDF417 detection (especially in low light)
      for (let i = 0; i < data.length; i += 4) {
        // Convert to grayscale
        const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        // Higher contrast for better detection (especially with torch)
        const contrast = 2.0; // Increased from 1.5 for better low-light detection
        const adjusted = ((gray - 128) * contrast) + 128;
        const clamped = Math.max(0, Math.min(255, adjusted));
        data[i] = clamped;     // R
        data[i + 1] = clamped; // G
        data[i + 2] = clamped; // B
        // Keep alpha channel
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      // Try native BarcodeDetector first if available
      let result = null;
      
      if (useNativeDetector && nativeDetectorRef.current) {
        try {
          console.log('Trying native BarcodeDetector...');
          const barcodes = await nativeDetectorRef.current.detect(canvas);
          if (barcodes && barcodes.length > 0) {
            const barcode = barcodes[0];
            result = {
              getText: () => barcode.rawValue,
              getBarcodeFormat: () => barcode.format
            };
            console.log('✅ Native BarcodeDetector found barcode:', barcode.format);
          }
        } catch (nativeErr) {
          console.log('Native detector failed, trying ZXing...', nativeErr);
        }
      }
      
      // Fallback to ZXing if native didn't work
      if (!result && codeReaderRef.current) {
        try {
          console.log('Trying ZXing with enhanced canvas...');
          const img1 = new Image();
          img1.crossOrigin = 'anonymous';
          img1.src = canvas.toDataURL('image/png', 1.0);
          
          await new Promise((resolve, reject) => {
            img1.onload = resolve;
            img1.onerror = reject;
            setTimeout(() => reject(new Error('Timeout')), 2000);
          });
          
          result = await codeReaderRef.current.decodeFromImage(img1);
          console.log('✅ Successfully decoded from enhanced canvas (ZXing)');
        } catch (err1) {
          console.log('Enhanced canvas failed, trying original...', err1.name);
          
          // Approach 2: Original frame
          try {
          const originalCanvas = document.createElement('canvas');
          originalCanvas.width = width;
          originalCanvas.height = height;
          const originalCtx = originalCanvas.getContext('2d');
          originalCtx.drawImage(video, 0, 0, width, height);
          
          const img2 = new Image();
          img2.crossOrigin = 'anonymous';
          img2.src = originalCanvas.toDataURL('image/png', 1.0);
          
          await new Promise((resolve, reject) => {
            img2.onload = resolve;
            img2.onerror = reject;
            setTimeout(() => reject(new Error('Timeout')), 2000);
          });
          
          result = await codeReaderRef.current.decodeFromImage(img2);
            console.log('✅ Successfully decoded from original frame');
          } catch (err2) {
            console.log('Original frame failed, trying higher contrast...', err2.name);
            
            // Approach 3: Higher contrast version
            try {
            const highContrastCanvas = document.createElement('canvas');
            highContrastCanvas.width = width;
            highContrastCanvas.height = height;
            const hcCtx = highContrastCanvas.getContext('2d');
            hcCtx.drawImage(video, 0, 0, width, height);
            
            const hcData = hcCtx.getImageData(0, 0, width, height);
            const hcPixels = hcData.data;
            for (let i = 0; i < hcPixels.length; i += 4) {
              const gray = hcPixels[i] * 0.299 + hcPixels[i + 1] * 0.587 + hcPixels[i + 2] * 0.114;
              const contrast = 2.5; // Very high contrast
              const adjusted = ((gray - 128) * contrast) + 128;
              const clamped = Math.max(0, Math.min(255, adjusted));
              hcPixels[i] = clamped;
              hcPixels[i + 1] = clamped;
              hcPixels[i + 2] = clamped;
            }
            hcCtx.putImageData(hcData, 0, 0);
            
            const img3 = new Image();
            img3.crossOrigin = 'anonymous';
            img3.src = highContrastCanvas.toDataURL('image/png', 1.0);
            
            await new Promise((resolve, reject) => {
              img3.onload = resolve;
              img3.onerror = reject;
              setTimeout(() => reject(new Error('Timeout')), 2000);
            });
            
            result = await codeReaderRef.current.decodeFromImage(img3);
            console.log('✅ Successfully decoded from high contrast frame');
            } catch (err3) {
              console.log('All capture attempts failed');
              throw err3;
            }
          }
        }
      }
      
      if (result) {
        const barcodeText = result.getText();
        console.log('✅ Barcode detected from captured frame!');
        console.log('Raw text length:', barcodeText.length);
        console.log('Raw text (first 200 chars):', barcodeText.substring(0, 200));
        
        const parsedData = parseAAMVA(barcodeText);
        console.log('Parsed data:', parsedData);

        const hasData = parsedData && (
          parsedData.firstName || 
          parsedData.lastName || 
          parsedData.driversLicense || 
          parsedData.address ||
          parsedData.city ||
          parsedData.state ||
          parsedData.zipcode
        );

        if (hasData) {
          stopScanning();
          // Play beep sound
          playBeepSound();
          if (onScanComplete) {
            onScanComplete(parsedData);
          }
        } else {
          console.warn('⚠️ Barcode detected but could not parse');
          setError('Could not parse barcode from captured frame. Try capturing again or use Upload Image.');
        }
      } else {
        setError('No barcode found in captured frame. Ensure the barcode is clearly visible and well-lit, then try capturing again.');
      }
    } catch (err) {
      console.error('❌ Error capturing/scanning frame:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      
      if (err.name === 'NotFoundException') {
        setError('Could not detect barcode in captured frame. Please:\n• Ensure the barcode is fully visible\n• Hold the license steady\n• Ensure good lighting\n• Try capturing again');
      } else {
        setError('Could not read barcode from captured frame: ' + (err.message || 'Unknown error') + '. Try again or use Upload Image.');
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const scanUploadedImage = async () => {
    if (!uploadedImage) {
      setError('Please upload an image first.');
      return;
    }

    if (!codeReaderRef.current) {
      setError('Scanner not initialized. Please refresh the page.');
      console.error('Code reader not initialized');
      return;
    }

    try {
      setError(null);
      console.log('Starting to scan uploaded image...');
      
      // Create an image element to decode
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Allow CORS if needed
      
      img.onload = async () => {
        try {
          console.log('Image loaded, dimensions:', img.width, 'x', img.height);
          
          // Try decoding from original image first (simpler, faster)
          let result;
          try {
            console.log('Attempting to decode from original image...');
            result = await codeReaderRef.current.decodeFromImage(img);
            console.log('✅ Successfully decoded from original image');
          } catch (originalError) {
            console.log('Original image decode failed, trying enhanced version...', originalError);
            
            // If original fails, try enhanced version
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Draw image to canvas
            ctx.drawImage(img, 0, 0);
            
            // Apply image enhancement for better barcode detection
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            
            // Convert to grayscale and increase contrast
            for (let i = 0; i < data.length; i += 4) {
              const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
              // Increase contrast
              const contrast = 1.5;
              const adjusted = ((gray - 128) * contrast) + 128;
              const clamped = Math.max(0, Math.min(255, adjusted));
              data[i] = clamped;     // R
              data[i + 1] = clamped; // G
              data[i + 2] = clamped; // B
              // Keep alpha channel
            }
            
            ctx.putImageData(imageData, 0, 0);
            
            // Try decoding from enhanced canvas
            result = await codeReaderRef.current.decodeFromImage(canvas);
            console.log('✅ Successfully decoded from enhanced canvas');
          }
          
          if (result) {
            const barcodeText = result.getText();
            console.log('✅ Barcode detected from image!');
            console.log('Raw text length:', barcodeText.length);
            console.log('Raw text (first 200 chars):', barcodeText.substring(0, 200));
            console.log('Barcode format:', result.getBarcodeFormat());
            
            // Parse AAMVA data
            const parsedData = parseAAMVA(barcodeText);
            console.log('Parsed data:', parsedData);

            const hasData = parsedData && (
              parsedData.firstName || 
              parsedData.lastName || 
              parsedData.driversLicense || 
              parsedData.address ||
              parsedData.city ||
              parsedData.state ||
              parsedData.zipcode
            );

            if (hasData) {
              console.log('✅ Data extracted successfully, calling onScanComplete');
              // Play beep sound
              playBeepSound();
              if (onScanComplete) {
                onScanComplete(parsedData);
              }
            } else {
              console.warn('⚠️ Could not parse barcode. Raw data (first 500 chars):', barcodeText.substring(0, 500));
              setError('Could not parse driver license data from the image. The barcode may not be visible or in an unsupported format. Check console for details.');
            }
          } else {
            setError('No barcode found in the uploaded image. Please ensure the image shows the barcode clearly.');
          }
        } catch (err) {
          console.error('❌ Error decoding from image:', err);
          console.error('Error details:', {
            name: err.name,
            message: err.message,
            stack: err.stack
          });
          
          if (err.name === 'NotFoundException') {
            setError('Could not detect barcode in the image. Please ensure:\n• The image is clear and in focus\n• The barcode is fully visible\n• There is good lighting\n• The image is not too dark or blurry\n• Try taking a new photo with better lighting');
          } else {
            setError('Could not read barcode from image: ' + (err.message || 'Unknown error') + '. Please try a different image or use manual entry.');
          }
        }
      };
      
      img.onerror = (error) => {
        console.error('Failed to load image:', error);
        setError('Failed to load the image. Please try uploading a different image.');
      };
      
      img.src = uploadedImage;
    } catch (err) {
      console.error('❌ Error scanning uploaded image:', err);
      setError('Failed to process the image: ' + (err.message || 'Unknown error') + '. Please try again.');
    }
  };

  return (
    <Modal>
      <div className="driver-license-scanner">
        <div className="scanner-header">
          <h2 className="scanner-title">Scan Driver's License</h2>
          <button className="scanner-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="scanner-content">
          {error && (
            <div className="scanner-error">
              {error}
            </div>
          )}

          {devices.length > 1 && (
            <div className="scanner-device-selector">
              <label htmlFor="camera-select">Select Camera:</label>
              <select
                id="camera-select"
                value={selectedDeviceId || ''}
                onChange={(e) => {
                  setSelectedDeviceId(e.target.value);
                  if (isScanning) {
                    stopScanning();
                  }
                }}
                disabled={isScanning}
              >
                {devices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Camera ${device.deviceId.substring(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="scanner-mode-selector" style={{ marginBottom: '16px' }}>
            <label style={{ marginRight: '16px' }}>
              <input
                type="radio"
                value="camera"
                checked={scanMode === 'camera'}
                onChange={(e) => {
                  setScanMode(e.target.value);
                  stopScanning();
                  setUploadedImage(null);
                }}
              />
              <span style={{ marginLeft: '8px' }}>Camera Scan</span>
            </label>
            <label>
              <input
                type="radio"
                value="upload"
                checked={scanMode === 'upload'}
                onChange={(e) => {
                  setScanMode(e.target.value);
                  stopScanning();
                }}
              />
              <span style={{ marginLeft: '8px' }}>Upload Image</span>
            </label>
          </div>

          {scanMode === 'camera' ? (
            <div className="scanner-video-container">
              <video
                ref={videoRef}
                className="scanner-video"
                style={{
                  display: isScanning ? 'block' : 'none',
                  width: '100%',
                  maxWidth: '640px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
                playsInline
                autoPlay
                muted
                id="scanner-video-element"
              />
              {isScanning && (
                <div className="scanner-alignment-box">
                  <div className="scanner-alignment-box-inner">
                    <div className="scanner-alignment-corner scanner-alignment-corner-tl"></div>
                    <div className="scanner-alignment-corner scanner-alignment-corner-tr"></div>
                    <div className="scanner-alignment-corner scanner-alignment-corner-bl"></div>
                    <div className="scanner-alignment-corner scanner-alignment-corner-br"></div>
                  </div>
                  <div className="scanner-alignment-hint">
                    Align barcode within the box
                  </div>
                </div>
              )}
              {!isScanning && (
                <div className="scanner-placeholder">
                  <p>Click "Start Scanning" to begin</p>
                  <p className="scanner-hint">
                    Position the driver's license barcode within the camera view
                  </p>
                  <p className="scanner-hint" style={{ marginTop: '12px', fontSize: '12px' }}>
                    Note: Some licenses have the barcode on the back. Try flipping the license.
                  </p>
                </div>
              )}
              {isScanning && (
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  textAlign: 'center'
                }}>
                  Hold steady and ensure barcode is clearly visible
                </div>
              )}
            </div>
          ) : (
            <div className="scanner-upload-container" style={{ 
              border: '2px dashed #ccc', 
              borderRadius: '8px', 
              padding: '40px', 
              textAlign: 'center',
              backgroundColor: '#f9f9f9'
            }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              {uploadedImage ? (
                <div>
                  <img 
                    src={uploadedImage} 
                    alt="Uploaded license" 
                    style={{ maxWidth: '100%', maxHeight: '300px', marginBottom: '16px' }}
                  />
                  <div>
                    <Button
                      type="secondary"
                      onClick={scanUploadedImage}
                      className="scanner-btn"
                      style={{ marginRight: '8px' }}
                    >
                      Scan This Image
                    </Button>
                    <Button
                      type="secondary"
                      onClick={() => {
                        setUploadedImage(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="scanner-btn"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ marginBottom: '16px' }}>Upload an image of the driver's license</p>
                  <Button
                    type="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    className="scanner-btn"
                  >
                    Choose Image
                  </Button>
                  <p style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
                    Upload a clear photo of the license (front or back with barcode)
                  </p>
                  <div style={{ marginTop: '12px', fontSize: '11px', color: '#888', textAlign: 'left', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <strong>Tips for best results:</strong>
                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                      <li>Ensure good lighting (avoid shadows)</li>
                      <li>Hold camera steady and in focus</li>
                      <li>Make sure the entire barcode is visible</li>
                      <li>Avoid glare or reflections</li>
                      <li>Use the back of the license (barcode is usually there)</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="scanner-actions" style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px', 
            justifyContent: 'center',
            width: '100%'
          }}>
            {!isScanning ? (
              <Button
                type="modalSave"
                onClick={startScanning}
                className="scanner-btn"
                disabled={isScanning}
                style={{ flex: '1 1 auto', minWidth: '120px' }}
              >
                Start Scanning
              </Button>
            ) : (
              <>
                <Button
                  type="secondary"
                  onClick={captureFrame}
                  className="scanner-btn"
                  style={{ 
                    backgroundColor: '#28a745', 
                    color: 'white',
                    flex: '1 1 auto',
                    minWidth: '140px',
                    fontSize: '13px',
                    padding: '8px 12px'
                  }}
                >
                  📸 Capture & Scan
                </Button>
                <Button
                  type="modalCancel"
                  onClick={stopScanning}
                  className="scanner-btn"
                  style={{ flex: '1 1 auto', minWidth: '120px' }}
                >
                  Stop Scanning
                </Button>
              </>
            )}
            <Button
              type="modalCancel"
              onClick={handleClose}
              className="scanner-btn"
              style={{ flex: '1 1 auto', minWidth: '120px' }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DriverLicenseScanner;
